import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { formatPrice } from "@/data/products";
import { Search, Package, Clock, CheckCircle2, XCircle } from "lucide-react";

const STATUS_ICON = {
  confirmed: Package, pending: Clock, shipped: Package, delivered: CheckCircle2, cancelled: XCircle,
};

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get("ref") || "");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  useSeo({
    title: "Track Your Order · BARIQUE",
    description: "Check the status of your Barique order with your order reference and email — no account required.",
    canonical: (typeof window !== "undefined" ? window.location.origin : "") + "/track",
    type: "website",
  });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setOrder(null);
    if (!ref.trim() || !email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/orders/track", { order_id: ref.trim().toUpperCase(), email: email.trim() });
      setOrder(data);
    } catch (e) {
      setError(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-32 pb-24 bg-ivory min-h-screen" data-testid="track-page">
      <div className="container-lux max-w-2xl">
        <p className="overline text-muted-foreground">Order Status</p>
        <h1 className="mt-4 font-serif tracking-tighter text-5xl md:text-6xl font-light">Track Your Order</h1>
        <p className="mt-6 text-sm text-muted-foreground max-w-lg">
          Enter your order reference (starts with <span className="font-mono text-ink">BQ</span>) and the email you used at checkout. No account required.
        </p>

        <form onSubmit={submit} className="mt-12 space-y-6" data-testid="track-form">
          <div>
            <label className="overline text-muted-foreground">Order Reference</label>
            <input
              className="lux-input font-mono uppercase tracking-widest"
              data-testid="track-ref"
              value={ref}
              onChange={(e) => setRef(e.target.value.toUpperCase())}
              placeholder="BQ1A2B3C4D"
              required
              autoFocus
            />
          </div>
          <div>
            <label className="overline text-muted-foreground">Email</label>
            <input
              type="email"
              className="lux-input"
              data-testid="track-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="track-submit"
            className="lux-btn-dark py-3 px-8 text-[10px] flex items-center gap-2 disabled:opacity-60"
          >
            <Search size={13} strokeWidth={1.5} /> {busy ? "Looking up…" : "Look Up Order"}
          </button>
        </form>

        {error && (
          <p className="mt-8 text-sm text-destructive" data-testid="track-error">{error}</p>
        )}

        {order && (
          <div className="mt-16" data-testid="track-result">
            <div className="divider-thin" />
            <div className="flex items-start justify-between gap-6 py-8 flex-wrap">
              <div>
                <p className="overline text-muted-foreground">Reference</p>
                <p className="font-mono text-lg mt-2">{order.id}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Placed {new Date(order.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                </p>
              </div>
              <StatusBadge status={order.status} paymentStatus={order.payment_status} />
            </div>
            <div className="divider-thin" />

            <div className="mt-10 space-y-6">
              {(order.items || []).map((i, idx) => (
                <div key={idx} className="flex gap-6" data-testid={`track-item-${idx}`}>
                  <div className="w-24 h-32 bg-muted overflow-hidden flex-shrink-0">
                    {i.image ? <img src={i.image} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="flex-1">
                    <p className="font-serif text-lg">{i.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {[i.size, i.color].filter(Boolean).join(" · ")}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Qty {i.quantity}</p>
                    <p className="mt-3 text-sm">{formatPrice((i.price || 0) * (i.quantity || 1))}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 flex justify-between items-baseline border-t border-border pt-6">
              <span className="overline">Total</span>
              <span className="font-serif text-2xl" data-testid="track-total">{formatPrice(order.total)}</span>
            </div>

            <p className="mt-10 text-xs text-muted-foreground">
              Have a question? <Link to="/about" className="lux-link">Message the atelier</Link>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status, paymentStatus }) {
  const Icon = STATUS_ICON[status] || Package;
  const paid = paymentStatus === "paid";
  return (
    <div className="flex flex-col items-end gap-2" data-testid="track-status">
      <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest px-4 py-2 bg-ink text-ivory">
        <Icon size={12} strokeWidth={1.5} /> {status || "Pending"}
      </span>
      <span className={`text-[10px] uppercase tracking-widest px-3 py-1 border ${paid ? "border-ink text-ink" : "border-border text-muted-foreground"}`}>
        Payment · {paymentStatus || "pending"}
      </span>
    </div>
  );
}
